import { Injectable, InjectionToken } from '@angular/core';
import { ApplicationContextPropertiesSet, Calendar, GuestService, HostService, Meeting } from '@vendasta/meetings';
import { Observable, of } from 'rxjs';
import { DailyAvailability, MeetingIntegrationServiceKeys, MeetingSchedulerIcons } from '../interface';
import { MeetingSchedulerServiceModule } from './meeting-scheduler-service.module';

// A Calendar represents either a Host (for S&SC a Salesperson) or a collection of individuals (for S&SC a team)
// id is filled in later by Meeting Scheduler by Meeting Scheduler meetingschedulerstoreservice
// externalId is either a business Id (account group id) or a sales team id.
export type ExternalCalendar = Pick<Calendar, 'id' | 'externalId' | 'displayName'>;
export enum Context {
  SMB = 'smb',
  PARTNER = 'partner',
}

export interface MeetingSchedulerCalendarService {
  getCalendars(): Observable<ExternalCalendar[]>;
}

@Injectable({ providedIn: MeetingSchedulerServiceModule })
export class StubMeetingSchedulerCalendarService implements MeetingSchedulerCalendarService {
  getCalendars(): Observable<ExternalCalendar[]> {
    return of([]);
  }
}

export interface MeetingNavigationLinkService {
  getPrimaryMeetingDetailsAction(meeting: Meeting[]): Observable<{ [meetingId: string]: MeetingDetailsActionOrLink }>;
  getSecondaryMeetingDetailsAction(
    meeting: Meeting[],
  ): Observable<{ [meetingId: string]: MeetingDetailsActionOrLink[] }>;
}

export interface MeetingNavigationLink {
  label: string;
  link: string;
}

export interface MeetingDetailsAction {
  label: string;
  onClick: (meeting: Meeting) => void;
  matIcon?: string;
}

export type MeetingDetailsActionOrLink = MeetingNavigationLink | MeetingDetailsAction;

export function isMeetingNavigationLink(m: MeetingDetailsActionOrLink): m is MeetingNavigationLink {
  return (m as MeetingNavigationLink).link && !(m as MeetingDetailsAction).onClick;
}

export function isMeetingDetailsAction(m: MeetingDetailsActionOrLink): m is MeetingNavigationLink {
  return !(m as MeetingNavigationLink).link && Boolean((m as MeetingDetailsAction).onClick);
}

@Injectable({ providedIn: MeetingSchedulerServiceModule })
export class StubMeetingNavigationLinkService implements MeetingNavigationLinkService {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getPrimaryMeetingDetailsAction(meeting: Meeting[]): Observable<{ [meetingId: string]: MeetingDetailsActionOrLink }> {
    return of({});
  }
  getSecondaryMeetingDetailsAction(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    meeting: Meeting[],
  ): Observable<{ [meetingId: string]: MeetingDetailsActionOrLink[] }> {
    return of({});
  }
}

export const USER_ID_TOKEN: InjectionToken<Observable<string>> = new InjectionToken(
  '[Meeting Scheduler]: Token for userId',
);
export const HOST_ID_TOKEN: InjectionToken<Observable<string>> = new InjectionToken(
  '[Meeting Scheduler]: Token for hostId',
);
export const CALENDAR_SLUG_TOKEN: InjectionToken<Observable<string>> = new InjectionToken(
  '[Meeting Scheduler]: Token for hostSlug',
);

export const GUEST_SERVICE_TOKEN: InjectionToken<GuestService> = new InjectionToken(
  '[Meeting Scheduler]: Token for GuestService',
);
export const HOST_SERVICE_TOKEN: InjectionToken<HostService> = new InjectionToken(
  '[Meeting Scheduler]: Token for HostService',
);
export const MEETING_SCHEDULER_CALENDAR_SERVICE_TOKEN: InjectionToken<MeetingSchedulerCalendarService> =
  new InjectionToken('[Meeting Scheduler]: Token for MeetingSchedulerCalendarService');
export const MEETING_SCHEDULER_MEETING_NAVIGATION_SERVICE_TOKEN: InjectionToken<MeetingNavigationLinkService> =
  new InjectionToken('[Meeting Scheduler]: Token for MeetingNavigationService');

// These recommended slugs are used when someone is first configuring calendars in Meeting Scheduler.
// Meeting Scheduler will prioritize slugs that appear first in the list.
// It will then choose the first slug that isn't already occupied by another host.
// For example, passing ['rlaforge', 'ryanlaforge', 'rlaforge1', 'ryanlaforge1']
// First, rlaforge will be looked at. If it's taken, ryanlaforge will be taken into account and so on.
// If all of the provided slugs are taken MeetingScheduler will not provide a recommendation.
export const RECOMMENDED_PERSONAL_CALENDAR_SLUGS_TOKEN = new InjectionToken<Observable<string[]>>(
  '[Meeting Scheduler]: Token for recommended personal calendar slugs',
);
export const RECOMMENDED_GROUP_CALENDAR_SLUGS_TOKEN = new InjectionToken<
  Observable<{ [externalCalendarId: string]: string[] }>
>('[Meeting Scheduler]: Token for recommended group calendar slugs');

export const INITIAL_AVAILABILITY_HOURS = new InjectionToken<Observable<DailyAvailability>>(
  '[Meeting Scheduler]: Token to determine whether to use navigation',
);
export const INITIAL_AVAILABILITY_HOURS_TIMEOUT = new InjectionToken<number>(
  '[Meeting Scheduler]: Timeout in milliseconds to wait for availability to load',
);

export const SETTINGS_LINK_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: Token for the settings link',
);
export const INTEGRATIONS_PAGE_LINK_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: Token for the settings link',
);
export const CALENDAR_PAGE_LINK_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: Token for the calendar page link',
);
export const CALENDAR_VIEW_PAGE_LINK_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: Token for the calendar page link',
);
export const EVENT_TYPE_PAGE_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: Token for the booking link',
);
export const GROUP_PAGE_TOKEN = new InjectionToken<Observable<string>>('[Meeting Scheduler]: Token for the group');
export const SERVICE_PAGE_TOKEN = new InjectionToken<Observable<string>>('[Meeting Scheduler]: Token for the service');
export const MEETING_LIST_PAGE_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: Token for the meeting list page link',
);
export const CRM_ACTIVITY_FEED_PAGE_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: Token for the crm activity feed page link',
);

export const CONTACT_PROFILE_PAGE_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: Token for the contacts profile page link',
);

export const SMS_CONFIGURATION_PAGE_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: Token for the SMS configuration page link',
);

export const SMS_ACTIVATION_PAGE_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: Token for the SMS activation page link',
);

export const MEETING_TYPE_LIST_VIEW_PAGE_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: Token for the meeting list table page link',
);

export const MEETING_SETTINGS_LIST_VIEW_PAGE_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: Token for the meeting settings page',
);

export const MEETING_INTEGRATION_SERVICE_KEYS_TOKEN = new InjectionToken<MeetingIntegrationServiceKeys>(
  '[Meeting Scheduler]: Token for integration service keys',
);

export const PERSONAL_CALENDAR_ID_TOKEN = new InjectionToken<Observable<string>>(
  '[Meeting Scheduler]: The current id for the personal calendar of the user',
);

export const APPLICATION_CONTEXT_TOKEN = new InjectionToken<ApplicationContextPropertiesSet>(
  '[Meeting Scheduler]: Current application context',
);

export interface ContextData {
  namespace: string;
  type: Context;
}

export const CONTEXT_DATA_TOKEN = new InjectionToken<Observable<ContextData>>(
  '[Meeting Scheduler]: Processed context data with namespace',
);

export const ALTERNATE_TRANSLATIONS_TOKEN = new InjectionToken<{ [translationKey: string]: string }>(
  '[Meeting Scheduler]: token for alternate translations',
);

export const ICONS_TOKEN = new InjectionToken<MeetingSchedulerIcons>(
  '[Meeting Scheduler]: token for the icons to be used in specific situations',
);

export const FEATURE_MEETING_DATE_RANGE_TOKEN = new InjectionToken<Observable<boolean>>(
  '[Meeting Scheduler]: token for whether date range can be shown',
);

export const FEATURE_MEETING_SESSION_EVENT_TOKEN = new InjectionToken<Observable<boolean>>(
  '[Meeting Scheduler]: token for whether session event type can be shown',
);

export const FEATURE_PRIORITY_ASSIGNMENT_TOKEN = new InjectionToken<Observable<boolean>>(
  '[Meeting Scheduler]: token for whether priority assignment can be shown',
);
export const FEATURE_SMS_ENABLED_TOKEN = new InjectionToken<Observable<boolean>>(
  '[Meeting Scheduler]: token for whether sms notification can be shown',
);

export const FEATURE_SMS_ACTIVATION_ENABLED_TOKEN = new InjectionToken<Observable<boolean>>(
  '[Meeting Scheduler]: token for whether the SMS activation button can be shown',
);

export const SMS_CONFIGURATION_VISIBLE_TOKEN = new InjectionToken<Observable<boolean>>(
  '[Meeting Scheduler]: token for whether the SMS configuration page is visible for this business',
);

export const FEATURE_TEAM_MEMBER_FILTER_TOKEN = new InjectionToken<Observable<boolean>>(
  '[Meeting Scheduler]: token for whether team member filter can be shown',
);
